// src/features/executive/shared/hooks/use-reference-data.ts
// Real-API hooks for reference lookups shared across executive + targets pages.
//
// Replaces the old `branches.mock.json` / `procedures.mock.json` imports,
// which were frozen to 3 branches / 7 procedures and made every dropdown
// silently mis-render once real clinic data was wired in.
//
// Each hook keeps a module-level Promise cache so concurrent components
// (e.g. FilterBranchSelect on multiple tabs) share a single fetch.

import { useEffect, useState } from 'react'
import BaseAPI from 'api/api'
import type { BranchRef, ProcedureRef } from 'api/executive/executive.types'
import { normalizeBranchParam } from 'features/executive/components/FilterBar/filter-config'

export interface PromotionRef {
  id: number
  name: string
}

export interface DoctorRef {
  id: number
  name: string
}

export interface OperativeTypeRef {
  id: number
  name: string
}

export interface CareerRef {
  id: number
  name: string
}

interface RefDataResult<T> {
  data: T[]
  loading: boolean
  error: Error | null
}

// ---- Branches -------------------------------------------------

interface BranchRow {
  branchId: number
  branchName: string
  branchNameEn?: string
  status?: string
}

let branchesPromise: Promise<BranchRef[]> | null = null

function fetchBranches(): Promise<BranchRef[]> {
  if (branchesPromise) return branchesPromise
  branchesPromise = BaseAPI.api
    .get('/branches', { params: { status: 'ACTIVE', pageLimit: 200 } })
    .then((res) => {
      const rows: BranchRow[] = Array.isArray(res.data) ? res.data : []
      return rows.map((b) => ({ id: b.branchId, name: b.branchName }))
    })
    .catch((err) => {
      // Reset so the next call retries instead of staying poisoned.
      branchesPromise = null
      throw err
    })
  return branchesPromise
}

export function useBranches(filter?: {
  scope?: 'own-branch' | 'all-branches'
  userBranchIds?: number[]
}): RefDataResult<BranchRef> {
  const [data, setData] = useState<BranchRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchBranches()
      .then((rows) => {
        if (!alive) return
        let filtered = rows
        if (filter?.scope !== 'all-branches' && filter?.userBranchIds?.length) {
          const allowed = new Set(filter.userBranchIds)
          filtered = rows.filter((b) => allowed.has(b.id))
        }
        setData(filtered)
        setError(null)
      })
      .catch((e) => {
        if (!alive) return
        setError(e instanceof Error ? e : new Error(String(e)))
        setData([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.scope, filter?.userBranchIds?.join(',')])

  return { data, loading, error }
}

// ---- Procedures (operatives) ---------------------------------

interface OperativeRow {
  operativeId: number
  operativeName: string
  operativeCode?: string
}

let proceduresPromise: Promise<ProcedureRef[]> | null = null

function fetchProcedures(): Promise<ProcedureRef[]> {
  if (proceduresPromise) return proceduresPromise
  proceduresPromise = BaseAPI.api
    .get('/operatives/all-active', { params: { pageLimit: 5000 } })
    .then((res) => {
      const rows: OperativeRow[] = Array.isArray(res.data) ? res.data : []
      // Every active operative is eligible for a procedure target — the
      // old mock's `trackedForTarget` flag only mattered when the dataset
      // was hand-curated.
      return rows.map((o) => ({
        id: o.operativeId,
        name: o.operativeName,
        code: o.operativeCode,
        trackedForTarget: true,
      }))
    })
    .catch((err) => {
      proceduresPromise = null
      throw err
    })
  return proceduresPromise
}

export function useProcedures(): RefDataResult<ProcedureRef> {
  const [data, setData] = useState<ProcedureRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchProcedures()
      .then((rows) => {
        if (!alive) return
        setData(rows)
        setError(null)
      })
      .catch((e) => {
        if (!alive) return
        setError(e instanceof Error ? e : new Error(String(e)))
        setData([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  return { data, loading, error }
}

// ---- Promotions ----------------------------------------------

interface PromotionRow {
  promotionId: number
  promotionName: string
  promotionNameEn?: string
}

let promotionsPromise: Promise<PromotionRef[]> | null = null

function fetchPromotions(): Promise<PromotionRef[]> {
  if (promotionsPromise) return promotionsPromise
  promotionsPromise = BaseAPI.api
    .get('/promotions', { params: { pageLimit: 500 } })
    .then((res) => {
      // /promotions paginated response: array body or { rows, count }.
      const data: any = res.data
      const rows: PromotionRow[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows) ? data.rows : []
      return rows.map((p) => ({ id: p.promotionId, name: p.promotionName }))
    })
    .catch((err) => {
      promotionsPromise = null
      throw err
    })
  return promotionsPromise
}

export function usePromotions(): RefDataResult<PromotionRef> {
  const [data, setData] = useState<PromotionRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchPromotions()
      .then((rows) => {
        if (!alive) return
        setData(rows)
        setError(null)
      })
      .catch((e) => {
        if (!alive) return
        setError(e instanceof Error ? e : new Error(String(e)))
        setData([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  return { data, loading, error }
}

// ---- Doctors -------------------------------------------------

interface DoctorRow {
  prefix?: string
  dentistId: number
  dentistFullname: string
}

let doctorsPromise: Promise<DoctorRef[]> | null = null

function fetchDoctors(): Promise<DoctorRef[]> {
  if (doctorsPromise) return doctorsPromise
  doctorsPromise = BaseAPI.api
    .get('/clinic/dentists')
    .then((res) => {
      const rows: DoctorRow[] = Array.isArray(res.data) ? res.data : []
      return rows.map((d) => ({
        id: d.dentistId,
        name: [d.prefix, d.dentistFullname].filter(Boolean).join(' ').trim() || `#${d.dentistId}`,
      }))
    })
    .catch((err) => {
      doctorsPromise = null
      throw err
    })
  return doctorsPromise
}

// Doctors attached to one branch (role_branches), via the schedule screen's
// dentist lookup. Not cached globally — the list is small and branch-specific.
interface BranchDoctorRow {
  userId: number
  fullname?: string
  firstname?: string
  lastname?: string
}

function fetchBranchDoctors(branchId: string): Promise<DoctorRef[]> {
  return BaseAPI.api
    .get(`doctor-schedules/dentists`, { params: { branchId } })
    .then((res) => {
      const rows: BranchDoctorRow[] = Array.isArray(res.data) ? res.data : []
      return rows.map((d) => ({
        id: d.userId,
        name: (d.fullname || [d.firstname, d.lastname].filter(Boolean).join(' ')).trim() || `#${d.userId}`,
      }))
    })
}

/**
 * Doctor options for the filter drawer. With one or several branches selected
 * the list is the doctors attached to any of them — one request with a
 * comma-list branchId (MD-2609-1162 TC-03); with all branches it is the whole
 * clinic. Name lookups (chips, empty states) call it without a branch so any
 * stored id still resolves to a name.
 */
export function useDoctors(branchId?: number | string | null): RefDataResult<DoctorRef> {
  const [data, setData] = useState<DoctorRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const scopedBranch = normalizeBranchParam(branchId)

  useEffect(() => {
    let alive = true
    setLoading(true)
    const request = scopedBranch != null ? fetchBranchDoctors(scopedBranch) : fetchDoctors()
    request
      .then((rows) => { if (alive) { setData(rows); setError(null) } })
      .catch((e) => { if (alive) { setError(e instanceof Error ? e : new Error(String(e))); setData([]) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [scopedBranch])

  return { data, loading, error }
}

// ---- Operative Types (treatment category) --------------------

interface OperativeTypeRow {
  operativeTypeId: number
  operativeTypeName: string
}

let operativeTypesPromise: Promise<OperativeTypeRef[]> | null = null

function fetchOperativeTypes(): Promise<OperativeTypeRef[]> {
  if (operativeTypesPromise) return operativeTypesPromise
  operativeTypesPromise = BaseAPI.api
    .get('/operative-types/all-active')
    .then((res) => {
      const rows: OperativeTypeRow[] = Array.isArray(res.data) ? res.data : []
      return rows.map((o) => ({ id: o.operativeTypeId, name: o.operativeTypeName }))
    })
    .catch((err) => {
      operativeTypesPromise = null
      throw err
    })
  return operativeTypesPromise
}

export function useOperativeTypes(): RefDataResult<OperativeTypeRef> {
  const [data, setData] = useState<OperativeTypeRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchOperativeTypes()
      .then((rows) => { if (alive) { setData(rows); setError(null) } })
      .catch((e) => { if (alive) { setError(e instanceof Error ? e : new Error(String(e))); setData([]) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  return { data, loading, error }
}

// ---- Careers --------------------------------------------------

interface CareerRow {
  careerId: number
  careerName: string
}

let careersPromise: Promise<CareerRef[]> | null = null

function fetchCareers(): Promise<CareerRef[]> {
  if (careersPromise) return careersPromise
  careersPromise = BaseAPI.api
    .get('/careers', { params: { status: 'ACTIVE', pageLimit: 10000 } })
    .then((res) => {
      const data: any = res.data
      const rows: CareerRow[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows) ? data.rows : []
      return rows
        .map((c) => ({ id: c.careerId, name: c.careerName }))
        .filter((c) => Number.isFinite(c.id) && c.id > 0 && c.name)
    })
    .catch((err) => {
      careersPromise = null
      throw err
    })
  return careersPromise
}

export function useCareers(): RefDataResult<CareerRef> {
  const [data, setData] = useState<CareerRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchCareers()
      .then((rows) => { if (alive) { setData(rows); setError(null) } })
      .catch((e) => { if (alive) { setError(e instanceof Error ? e : new Error(String(e))); setData([]) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  return { data, loading, error }
}

// ---- Test helpers --------------------------------------------

export const __resetReferenceCache = () => {
  branchesPromise = null
  proceduresPromise = null
  promotionsPromise = null
  doctorsPromise = null
  operativeTypesPromise = null
  careersPromise = null
}
